import {
  CAPTURE_FIXTURE_SET_NAME,
  STEP_COMMAND_FILE,
  STEP_TEST_CASES_DIR,
  STEP_TEST_CLOCK_SUFFIX,
  STEP_TEST_DIFF_ACTUAL,
  STEP_TEST_DIFF_BEGIN,
  STEP_TEST_DIFF_END,
  STEP_TEST_DIFF_EXPECTED,
  STEP_TEST_FROZEN_TIME_ENV,
  STEP_TEST_FROZEN_TIME_FILE,
  STEP_TEST_REQUEST_SERIALIZER_FILE,
  STEP_TEST_REQUESTS_DIVERGED_PREFIX,
  STEP_TEST_REQUESTS_FILE,
  STEP_TEST_RESULTS_SENTINEL,
} from "./constants";
import { STEP_TEST_CONTROL_HOST, STEP_TEST_REQUESTS_SUFFIX } from "./requests";
import {
  bytesAreUtf8Text,
  STEP_TEST_SQLITE_DUMP_SENTINEL,
  STEP_TEST_VOLUME_BASE64_SENTINEL,
} from "./volumes";

export const STEP_TEST_RUNNER_FILE = "__runStepTests.sh";
export const STEP_TEST_RUN_COMMAND = `sh ${STEP_TEST_RUNNER_FILE}`;
export const STEP_TEST_CAPTURE_PURPOSE = "step_test_capture";
export const STEP_TEST_CAPTURE_RUNNER_FILE = "__captureStepTest.sh";
export const STEP_TEST_CAPTURE_RUN_COMMAND = `sh ${STEP_TEST_CAPTURE_RUNNER_FILE}`;
export const STEP_TEST_VOLUME_LIST_FILE = "__stepTestVolumes";
export const STEP_TEST_RO_VOLUME_LIST_FILE = "__stepTestReadOnlyVolumes";
export const STEP_TEST_CAPTURE_SENTINEL = "__3B_STEP_TEST_CAPTURE__";
export const STEP_TEST_FREEZE_TIME_SHIM_PATH =
  "/usr/local/lib/3b-freeze-time.so";

// Shared by both scripts so capture and replay never disagree on how a fixture becomes a volume file; references $STORAGE_ROOT, $VOLUME_BASE64_SENTINEL, and $_RO_VOLUMES from each script's preamble.
const VOLUME_SHELL_HELPERS = `_volume_root() {
  printf 'tests/step-tests/%s.volumes' "$1"
}

_clear_volume() {
  _target="$STORAGE_ROOT/$1"
  mkdir -p "$_target" || return 1
  find "$_target" -mindepth 1 -exec rm -rf {} +
}

_base64_decode() {
  if printf '' | base64 -d >/dev/null 2>&1; then
    base64 -d
  else
    base64 -D
  fi
}

_is_sqlite_file() {
  head -c 15 "$1" 2>/dev/null | grep -q "SQLite format 3"
}

# Orders each table's rows by every column's ordinal position (from pragma_table_info) so two databases with the same logical rows in different insertion order dump byte-identical; fails non-zero on a virtual table or any sqlite3 error.
_sqlite_canonical_dump() {
  _db="$1"
  _out="$2"
  _dump_sql_script=$(mktemp) || return 1
  _schema_sql=$(sqlite3 "$_db" "SELECT sql FROM sqlite_master WHERE sql IS NOT NULL AND name NOT LIKE 'sqlite_%' ORDER BY rowid;" 2>/dev/null) || {
    rm -f "$_dump_sql_script"
    return 1
  }
  case "$_schema_sql" in
    *"CREATE VIRTUAL TABLE"*)
      rm -f "$_dump_sql_script"
      return 1
      ;;
  esac
  {
    echo ".headers off"
    echo ".mode list"
    echo "SELECT 'PRAGMA foreign_keys=OFF;';"
    echo "SELECT 'BEGIN TRANSACTION;';"
    echo "SELECT sql || ';' FROM sqlite_master WHERE type = 'table' AND sql IS NOT NULL AND name NOT LIKE 'sqlite_%' ORDER BY rowid;"
  } > "$_dump_sql_script"
  _tables=$(sqlite3 "$_db" "SELECT name FROM sqlite_master WHERE type='table' AND sql IS NOT NULL AND name NOT LIKE 'sqlite_%' ORDER BY rowid;" 2>/dev/null) || {
    rm -f "$_dump_sql_script"
    return 1
  }
  # A table name with whitespace or shell-glob characters word-splits here, failing the dump and falling back to base64 capture, which stays consistent between capture and replay.
  for _table in $_tables; do
    _column_order=$(sqlite3 "$_db" "SELECT group_concat(printf('%d', cid + 1), ',') FROM (SELECT cid FROM pragma_table_info('$_table') ORDER BY cid);" 2>/dev/null) || {
      rm -f "$_dump_sql_script"
      return 1
    }
    {
      echo ".mode insert \\"$_table\\""
      echo "SELECT * FROM \\"$_table\\" ORDER BY $_column_order;"
      echo ".mode list"
    } >> "$_dump_sql_script"
  done
  _has_sequence=$(sqlite3 "$_db" "SELECT 1 FROM sqlite_master WHERE name='sqlite_sequence';" 2>/dev/null) || {
    rm -f "$_dump_sql_script"
    return 1
  }
  if [ "$_has_sequence" = "1" ]; then
    {
      # The restored table's own AUTOINCREMENT inserts implicitly repopulate sqlite_sequence, so without this DELETE the explicit rows below would double up rather than replace them.
      echo "SELECT 'DELETE FROM sqlite_sequence;';"
      echo ".mode insert sqlite_sequence"
      echo "SELECT * FROM sqlite_sequence ORDER BY 1,2;"
      echo ".mode list"
    } >> "$_dump_sql_script"
  fi
  # Indexes, triggers, and views are deferred until after the data so a trigger firing during restore never observes a table it depends on as still-empty.
  echo "SELECT sql || ';' FROM sqlite_master WHERE type != 'table' AND sql IS NOT NULL AND name NOT LIKE 'sqlite_%' ORDER BY rowid;" >> "$_dump_sql_script"
  echo "SELECT 'COMMIT;';" >> "$_dump_sql_script"
  sqlite3 "$_db" < "$_dump_sql_script" > "$_out" 2>/dev/null
  _dump_status=$?
  rm -f "$_dump_sql_script"
  return "$_dump_status"
}

_materialize_sqlite_dump() {
  _fixture="$1"
  _target="$2"
  mkdir -p "\${_target%/*}" || return 1
  _dump_tmp=$(mktemp) || return 1
  sed '1d' "$_fixture" > "$_dump_tmp"
  rm -f "$_target"
  sqlite3 "$_target" < "$_dump_tmp"
  _materialize_status=$?
  rm -f "$_dump_tmp"
  return "$_materialize_status"
}

_fixture_to_file() {
  _source="$1"
  _target="$2"
  mkdir -p "\${_target%/*}" || return 1
  _first_line=$(sed -n '1p' "$_source")
  if [ "$_first_line" = "$VOLUME_BASE64_SENTINEL" ]; then
    sed '1d' "$_source" | _base64_decode > "$_target"
  elif [ "$_first_line" = "$VOLUME_SQLITE_DUMP_SENTINEL" ]; then
    _materialize_sqlite_dump "$_source" "$_target"
  else
    cp "$_source" "$_target"
  fi
}

_seed_volumes() {
  _root="$(_volume_root "$1")/initial"
  [ -d "$_root" ] || return 0
  for _file in $(find "$_root" -type f | sort); do
    _rel=\${_file#$_root/}
    _target="$STORAGE_ROOT/$_rel"
    _fixture_to_file "$_file" "$_target" || return 1
  done
}

_volume_message=""
_volume_fail() {
  if [ -z "$_volume_message" ]; then _volume_message="$1"; fi
}

# Compares one volume's on-disk state against a single fixture-phase dir (either
# phase's directory shape is identical, so replay's expected/initial fallback and
# capture's read-only check both drive this one code path); excludes SQLite's
# -wal/-shm from comparison since WAL mode writes a random checkpoint salt into
# -wal and -shm is transient scratch, so neither ever byte-matches between runs.
_compare_volume_against() {
  _vol="$1"
  _fixture_vol="$2"
  if [ -d "$_fixture_vol" ]; then
    for _fixture_file in $(find "$_fixture_vol" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort); do
      _rel=\${_fixture_file#$_fixture_vol/}
      _actual_file_path="$STORAGE_ROOT/$_vol/$_rel"
      _decoded_fixture=".step-test-expected/$_vol/$_rel"
      if [ ! -f "$_actual_file_path" ]; then
        _volume_fail "missing volume file /storage/$_vol/$_rel"
      elif [ "$(sed -n '1p' "$_fixture_file")" = "$VOLUME_SQLITE_DUMP_SENTINEL" ]; then
        _actual_dump=".step-test-actual-dump/$_vol/$_rel"
        _fixture_db=".step-test-fixture-db/$_vol/$_rel"
        _fixture_dump=".step-test-fixture-dump/$_vol/$_rel"
        mkdir -p "\${_actual_dump%/*}" "\${_fixture_db%/*}" "\${_fixture_dump%/*}"
        if ! _sqlite_canonical_dump "$_actual_file_path" "$_actual_dump" \\
          || ! _materialize_sqlite_dump "$_fixture_file" "$_fixture_db" \\
          || ! _sqlite_canonical_dump "$_fixture_db" "$_fixture_dump" \\
          || ! cmp -s "$_fixture_dump" "$_actual_dump"; then
          _volume_fail "volume file /storage/$_vol/$_rel did not match expected content"
        fi
        rm -f "$_actual_dump" "$_fixture_db" "$_fixture_dump"
      elif ! _fixture_to_file "$_fixture_file" "$_decoded_fixture"; then
        _volume_fail "could not prepare expected volume file /storage/$_vol/$_rel"
      elif ! cmp -s "$_decoded_fixture" "$_actual_file_path"; then
        _volume_fail "volume file /storage/$_vol/$_rel did not match expected content"
      fi
    done
  fi
  _actual_vol="$STORAGE_ROOT/$_vol"
  if [ -d "$_actual_vol" ]; then
    for _actual_file in $(find "$_actual_vol" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort); do
      _rel=\${_actual_file#$_actual_vol/}
      if [ ! -f "$_fixture_vol/$_rel" ]; then
        _volume_fail "unexpected volume file /storage/$_vol/$_rel"
      fi
    done
  fi
}

_is_ro_volume() {
  for _ro_vol in $_RO_VOLUMES; do
    [ "$_ro_vol" = "$1" ] && return 0
  done
  return 1
}

_serialize_volume() {
  sh tests/serialize "$1" > "$2"
}`;

// Bun ignores BUN_PRELOAD, so BUN_OPTIONS is the only env var that injects the determinism preload into every bun invocation (env-prefixed, absolute-path, compound).
const BUN_PRELOAD_SHELL = `if [ -n "$BUN_PRELOAD" ]; then
  BUN_OPTIONS="--preload=$BUN_PRELOAD\${BUN_OPTIONS:+ $BUN_OPTIONS}"
  export BUN_OPTIONS
fi`;

// Talks to the proxy's replay control endpoint (services/proxy/src/replay.rs)
// to begin/end a fixture-set replay session, via curl + jq — the HTTP tooling
// the sandbox rootfs actually ships (services/worker/Dockerfile.sandboxd);
// there is no bun/node there. Strips the determinism preload/env so the
// control call itself isn't subject to the frozen clock it's arranging for
// the step. On `end`, ANY failure (curl error, empty or non-JSON body, jq
// missing, anything but an explicit ok:true) prints a non-empty message to
// stdout — the caller only sees stdout, so silence must always mean "passed".
// The control host is MITM'd like any other HTTPS destination the sandbox
// reaches (services/proxy/src/transparent.rs terminates every CONNECT before
// routing on hostname), so this rides the same TLS termination as real
// egress rather than a bespoke exemption. STEP_TEST_CONTROL_URL only overrides
// the default for tests, which stub the endpoint with a plain local HTTP
// server, so the HTTPS restriction applies to the real default only.
const REPLAY_CTL_SHELL_HELPERS = `_replay_ctl() {
  if [ -n "$STEP_TEST_CONTROL_URL" ]; then
    _replay_ctl_url="$STEP_TEST_CONTROL_URL/$1?test=$2"
    _replay_ctl_tls_flags=""
  else
    _replay_ctl_url="https://${STEP_TEST_CONTROL_HOST}/$1?test=$2"
    _replay_ctl_tls_flags="--proto =https --tlsv1.2"
  fi
  _replay_ctl_body=$(env -u LD_PRELOAD -u BUN_OPTIONS -u ${STEP_TEST_FROZEN_TIME_ENV} \\
    curl -fsS $_replay_ctl_tls_flags -X POST --max-time 10 "$_replay_ctl_url" 2>/dev/null)
  _replay_ctl_status=$?
  [ "$1" = "end" ] || return 0
  if [ "$_replay_ctl_status" != 0 ] || [ -z "$_replay_ctl_body" ]; then
    printf '%s' "step-test replay control call failed"
    return 0
  fi
  _replay_ctl_message=$(printf '%s' "$_replay_ctl_body" | jq -r '
    if .ok == true then ""
    elif .ok == false then
      (if (.message | type) == "string" and .message != "" then .message
       else "step-test replay control call failed" end)
    else "step-test replay control call failed" end' 2>/dev/null)
  if [ $? -ne 0 ]; then
    printf '%s' "step-test replay control call failed"
    return 0
  fi
  printf '%s' "$_replay_ctl_message"
}`;

// Started only when a test has both a `.requests` fixture and a step-authored
// tests/serialize-request; the proxy can't run step scripts itself, so this
// sh + curl + jq loop long-polls the control host for a live request that
// needs a match key, pipes it through the step's serializer, and posts the
// key back. The long poll itself is the wait; the only sleep is a 1s backoff
// after a curl failure so a dead control channel doesn't hot-spin. The
// serializer always receives the request body base64-encoded
// (bodyEncoding:"base64") — capture-side key computation below uses the same
// contract so keys compare equal.
const NORMALIZE_HELPER_SHELL = `_NORMALIZE_HELPER_PID=""

_normalize_helper_start() {
  (
    _nh_base="\${STEP_TEST_CONTROL_URL:-http://${STEP_TEST_CONTROL_HOST}}"
    _nh_err=$(mktemp) || exit 1
    while :; do
      _nh_resp=$(env -u LD_PRELOAD -u BUN_OPTIONS -u ${STEP_TEST_FROZEN_TIME_ENV} \\
        curl -fsS -X POST --max-time 35 "$_nh_base/normalize/poll" 2>/dev/null)
      if [ $? -ne 0 ]; then
        sleep 1
        continue
      fi
      _nh_id=$(printf '%s' "$_nh_resp" | jq -c '.pending.id // empty' 2>/dev/null)
      [ -n "$_nh_id" ] || continue
      _nh_stdin=$(printf '%s' "$_nh_resp" | jq -c '{
        method: .pending.request.method,
        url: .pending.request.url,
        headers: (.pending.request.headers // []),
        body: (.pending.request.body // ""),
        bodyEncoding: "base64"
      }' 2>/dev/null)
      [ -n "$_nh_stdin" ] || continue
      if _nh_key=$(printf '%s' "$_nh_stdin" | sh tests/serialize-request 2>"$_nh_err"); then
        _nh_reply=$(jq -cn --argjson id "$_nh_id" --arg key "$_nh_key" '{id: $id, key: $key}')
      else
        _nh_reply=$(jq -cn --argjson id "$_nh_id" \\
          --arg error "tests/serialize-request failed: $(head -c 500 "$_nh_err")" \\
          '{id: $id, error: $error}')
      fi
      env -u LD_PRELOAD -u BUN_OPTIONS -u ${STEP_TEST_FROZEN_TIME_ENV} \\
        curl -fsS -X POST --max-time 10 -H 'content-type: application/json' \\
        -d "$_nh_reply" "$_nh_base/normalize/reply" >/dev/null 2>&1
    done
  ) &
  _NORMALIZE_HELPER_PID=$!
}

_normalize_helper_stop() {
  [ -n "$_NORMALIZE_HELPER_PID" ] || return 0
  kill "$_NORMALIZE_HELPER_PID" 2>/dev/null
  wait "$_NORMALIZE_HELPER_PID" 2>/dev/null
  _NORMALIZE_HELPER_PID=""
}`;

// Replays each tests/step-tests/<name>.input through the step's real entry
// command and compares stdout to <name>.output ($(...) strips trailing
// newlines on both sides, which is the only normalization). Pure POSIX sh —
// the one runtime present in every step image.
export const STEP_TEST_RUNNER_SH = `#!/bin/sh
SENTINEL="${STEP_TEST_RESULTS_SENTINEL}"
DIFF_BEGIN="${STEP_TEST_DIFF_BEGIN}"
DIFF_EXPECTED="${STEP_TEST_DIFF_EXPECTED}"
DIFF_ACTUAL="${STEP_TEST_DIFF_ACTUAL}"
DIFF_END="${STEP_TEST_DIFF_END}"
STORAGE_ROOT="\${STEP_TEST_STORAGE_ROOT:-/storage}"
VOLUME_BASE64_SENTINEL="${STEP_TEST_VOLUME_BASE64_SENTINEL}"
VOLUME_SQLITE_DUMP_SENTINEL="${STEP_TEST_SQLITE_DUMP_SENTINEL}"
FREEZE_SHIM="\${STEP_TEST_FREEZE_TIME_SHIM:-${STEP_TEST_FREEZE_TIME_SHIM_PATH}}"
_STEP_CMD="$(cat ${STEP_COMMAND_FILE})"
_DECLARED_VOLUMES=""
[ -f "${STEP_TEST_VOLUME_LIST_FILE}" ] && _DECLARED_VOLUMES="$(cat "${STEP_TEST_VOLUME_LIST_FILE}")"
_RO_VOLUMES=""
[ -f "${STEP_TEST_RO_VOLUME_LIST_FILE}" ] && _RO_VOLUMES="$(cat "${STEP_TEST_RO_VOLUME_LIST_FILE}")"
_HAS_REQUEST_SERIALIZER=0
[ -f "${STEP_TEST_REQUEST_SERIALIZER_FILE}" ] && _HAS_REQUEST_SERIALIZER=1
_specs=""
_fail=0

${BUN_PRELOAD_SHELL}

_json_escape() {
  printf '%s' "$1" | tr '[:cntrl:]' ' ' | sed 's/\\\\/\\\\\\\\/g; s/"/\\\\"/g'
}

_record() {
  _n=$(_json_escape "$1")
  _m=$(_json_escape "$3")
  _obj="{\\"name\\":\\"$_n\\",\\"status\\":\\"$2\\",\\"message\\":\\"$_m\\"}"
  if [ -z "$_specs" ]; then _specs="$_obj"; else _specs="$_specs,$_obj"; fi
}

${VOLUME_SHELL_HELPERS}

${REPLAY_CTL_SHELL_HELPERS}

${NORMALIZE_HELPER_SHELL}

_asserted_volumes() {
  _root=$(_volume_root "$1")
  for _phase in initial expected; do
    _dir="$_root/$_phase"
    [ -d "$_dir" ] || continue
    for _vol in "$_dir"/*; do
      [ -d "$_vol" ] || continue
      printf '%s\\n' "\${_vol##*/}"
    done
  done | sort -u
}

_prepare_volumes() {
  for _vol in $(
    { printf '%s\\n' $_DECLARED_VOLUMES; _asserted_volumes "$1"; } | sort -u
  ); do
    _clear_volume "$_vol" || return 1
  done
  _seed_volumes "$1"
}

_load_frozen_time() {
  _clock_file="${STEP_TEST_CASES_DIR}/$1${STEP_TEST_CLOCK_SUFFIX}"
  if [ -f "$_clock_file" ]; then
    ${STEP_TEST_FROZEN_TIME_ENV}="$(cat "$_clock_file")"
    export ${STEP_TEST_FROZEN_TIME_ENV}
    if [ -f "$FREEZE_SHIM" ]; then
      export LD_PRELOAD="$FREEZE_SHIM"
    fi
  else
    unset ${STEP_TEST_FROZEN_TIME_ENV}
    unset LD_PRELOAD
  fi
}

_compare_serialized() {
  _vol="$1"
  _snapshot_file="$2"
  if [ ! -f tests/serialize ]; then
    _volume_fail "missing tests/serialize for volume $_vol"
    return
  fi
  _actual_serialized=".step-test-actual-serialized/$_vol"
  mkdir -p "\${_actual_serialized%/*}"
  if ! _serialize_volume "$_vol" "$_actual_serialized"; then
    _volume_fail "tests/serialize failed for volume $_vol"
  elif ! cmp -s "$_snapshot_file" "$_actual_serialized"; then
    _volume_fail "volume file /storage/$_vol did not match expected content"
  fi
  rm -f "$_actual_serialized"
}

# A volume with an expected/ fixture is compared against it as before; a
# listed read-only volume with no expected/ fixture is instead compared
# against its initial/ fixture, asserting the step left it untouched. A
# volume with an expected/<vol>.serialized snapshot is instead compared via
# the step's tests/serialize output.
_compare_volumes() {
  _volume_message=""
  _root=$(_volume_root "$1")
  for _vol in $(_asserted_volumes "$1"); do
    if [ -f "$_root/expected/$_vol.serialized" ]; then continue; fi
    _expected_vol="$_root/expected/$_vol"
    _initial_vol="$_root/initial/$_vol"
    if [ ! -d "$_expected_vol" ] && _is_ro_volume "$_vol" && [ -d "$_initial_vol" ]; then
      _compare_volume_against "$_vol" "$_initial_vol"
    else
      _compare_volume_against "$_vol" "$_expected_vol"
    fi
  done
  for _snapshot_file in "$_root"/expected/*.serialized; do
    [ -f "$_snapshot_file" ] || continue
    _vol=\${_snapshot_file##*/}
    _vol=\${_vol%.serialized}
    _compare_serialized "$_vol" "$_snapshot_file"
  done
  [ -z "$_volume_message" ]
}

_emit() {
  printf '%s {"specs":[%s]}\\n' "$SENTINEL" "$_specs"
  exit "$_fail"
}
trap _emit EXIT

# When any test declares fixtures the orchestrator shares one writable throwaway fork across the whole batch, so each test must be reset to pristine branch state or it would see what an earlier test wrote.
_SNAPSHOT_DIR=".step-test-volume-snapshot"
_FIXTURE_MODE=0
for _dir in tests/step-tests/*.volumes; do
  [ -d "$_dir" ] || continue
  _FIXTURE_MODE=1
  break
done
if [ "$_FIXTURE_MODE" = 1 ]; then
  mkdir -p "$_SNAPSHOT_DIR"
  cp -a "$STORAGE_ROOT/." "$_SNAPSHOT_DIR/" || _SNAPSHOT_FAILED=1
fi

_reset_volumes() {
  [ "$_FIXTURE_MODE" = 1 ] || return 0
  [ -n "$_SNAPSHOT_FAILED" ] && return 1
  # /storage/<vol> are mount points whose dir can't be removed, so rm -rf each entry (clears mount contents, removes scratch) then restore contents from the snapshot without touching the mount dirs.
  for _entry in "$STORAGE_ROOT"/*; do
    [ -e "$_entry" ] || continue
    rm -rf "$_entry" 2>/dev/null
    # rm can't unlink the mount-point dir itself, so verify the dir is empty rather than checking rm's status (which always fails on the mount); a leftover (e.g. a file under a dir the step made read-only via chmod 555) means the volume didn't clear.
    [ -z "$(ls -A "$_entry" 2>/dev/null)" ] || return 1
  done
  for _snap in "$_SNAPSHOT_DIR"/*; do
    [ -e "$_snap" ] || continue
    _dest="$STORAGE_ROOT/\${_snap##*/}"
    # Plain recursive copy (no -a): restoring content only, since -a would try to set times on the /storage/<vol> mount dir and fail.
    if [ -d "$_snap" ]; then
      mkdir -p "$_dest" && cp -R "$_snap/." "$_dest/" || return 1
    else
      cp "$_snap" "$_dest" || return 1
    fi
  done
  return 0
}

_found=0
for _input in tests/step-tests/*.input; do
  [ -e "$_input" ] || continue
  _found=1
  _name=\${_input##*/}
  _name=\${_name%.input}
  _out_file="tests/step-tests/$_name.output"
  if [ ! -f "$_out_file" ]; then
    _record "$_name" failed "missing $_out_file"
    _fail=1
    continue
  fi
  if ! _reset_volumes; then
    _record "$_name" failed "could not reset volume fixtures"
    _fail=1
    continue
  fi
  if ! _prepare_volumes "$_name"; then
    _record "$_name" failed "could not prepare volume fixtures"
    _fail=1
    continue
  fi
  _load_frozen_time "$_name"
  _requests_file="tests/step-tests/$_name${STEP_TEST_REQUESTS_SUFFIX}"
  _has_requests=0
  [ -f "$_requests_file" ] && _has_requests=1
  _use_normalize_helper=0
  if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
    _use_normalize_helper=1
    _normalize_helper_start
  fi
  [ "$_has_requests" = 1 ] && _replay_ctl begin "$_name" >/dev/null
  if _actual=$(sh -c "$_STEP_CMD" < "$_input"); then
    _step_status=0
  else
    _step_status=$?
  fi
  _replay_message=""
  [ "$_has_requests" = 1 ] && _replay_message=$(_replay_ctl end "$_name")
  [ "$_use_normalize_helper" = 1 ] && _normalize_helper_stop
  if [ "$_step_status" = 0 ]; then
    _expected=$(cat "$_out_file")
    if [ "$_expected" = "$_actual" ] && _compare_volumes "$_name" && [ -z "$_replay_message" ]; then
      _record "$_name" passed ""
    else
      if [ "$_expected" = "$_actual" ]; then
        if [ -n "$_replay_message" ]; then
          _record "$_name" failed "$_replay_message"
        else
          _record "$_name" failed "$_volume_message"
        fi
      else
        _mismatch="expected [$_expected] but got [$_actual]"
        [ -n "$_replay_message" ] && _mismatch="$_mismatch; requests diverged — $_replay_message"
        _record "$_name" failed "$_mismatch"
        printf '%s %s\\n' "$DIFF_BEGIN" "$_name"
        printf '%s\\n' "$DIFF_EXPECTED"
        printf '%s\\n' "$_expected"
        printf '%s\\n' "$DIFF_ACTUAL"
        printf '%s\\n' "$_actual"
        printf '%s\\n' "$DIFF_END"
      fi
      _fail=1
    fi
  else
    _mismatch="step exited with code $_step_status"
    [ -n "$_replay_message" ] && _mismatch="$_mismatch; requests diverged — $_replay_message"
    _record "$_name" failed "$_mismatch"
    _fail=1
  fi
done

if [ "$_found" = 0 ]; then
  echo "No step tests found under tests/step-tests/. Add one first." >&2
  _fail=1
fi
`;

type StepFile = { path: string; content: string };

/**
 * Run-time file set for a `step_test` execution: the step's code (unchanged,
 * so the build cache still matches), its tests/ files, the injected runner,
 * and the step's original command. Build layers are keyed on `codeFiles`
 * alone, so the extra files never bust the cache.
 */
export function assembleStepTestRunFiles(args: {
  codeFiles: StepFile[];
  testFiles: StepFile[];
  stepCommand: string;
  volumes: readonly string[];
  roVolumes: readonly string[];
}): StepFile[] {
  return [
    ...args.codeFiles,
    ...args.testFiles,
    { path: STEP_TEST_RUNNER_FILE, content: STEP_TEST_RUNNER_SH },
    { path: STEP_COMMAND_FILE, content: args.stepCommand },
    { path: STEP_TEST_VOLUME_LIST_FILE, content: args.volumes.join("\n") },
    { path: STEP_TEST_RO_VOLUME_LIST_FILE, content: args.roVolumes.join("\n") },
  ];
}

// Runs the step once against a single live input, seeding declared volumes
// first, then emits the step’s final stdout and volume bytes as base64 so
// the caller can save them as a step test’s expected fixtures.
export const STEP_TEST_CAPTURE_SH = `#!/bin/sh
SENTINEL="${STEP_TEST_CAPTURE_SENTINEL}"
STORAGE_ROOT="\${STEP_TEST_STORAGE_ROOT:-/storage}"
VOLUME_BASE64_SENTINEL="${STEP_TEST_VOLUME_BASE64_SENTINEL}"
VOLUME_SQLITE_DUMP_SENTINEL="${STEP_TEST_SQLITE_DUMP_SENTINEL}"
FREEZE_SHIM="\${STEP_TEST_FREEZE_TIME_SHIM:-${STEP_TEST_FREEZE_TIME_SHIM_PATH}}"
_STEP_CMD="$(cat ${STEP_COMMAND_FILE})"
_VOLUMES="$(cat ${STEP_TEST_VOLUME_LIST_FILE})"
_RO_VOLUMES=""
[ -f "${STEP_TEST_RO_VOLUME_LIST_FILE}" ] && _RO_VOLUMES="$(cat "${STEP_TEST_RO_VOLUME_LIST_FILE}")"
_SPEC="${CAPTURE_FIXTURE_SET_NAME}"

${BUN_PRELOAD_SHELL}

${VOLUME_SHELL_HELPERS}

${REPLAY_CTL_SHELL_HELPERS}

${NORMALIZE_HELPER_SHELL}

_base64() {
  base64 | tr -d '\\n'
}

# Runs before \`begin\`: computes each recorded fixture's serializer match key
# (double-run to guard determinism, mirroring the tests/serialize check below)
# and registers them with the proxy so replay matches by key instead of raw
# method+url+body. Feeds the serializer the same shape as the replay-side
# normalize helper (recorded request headers, body always base64-encoded), so
# a key computed here compares equal to one computed from the live request.
# Writes its own \`fixtureKeys\` sentinel line to stdout so the pin flow can
# persist the keys alongside the fixtures.
_compute_and_register_fixture_keys() {
  _fk_file="$1"
  _fk_base="\${STEP_TEST_CONTROL_URL:-http://${STEP_TEST_CONTROL_HOST}}"
  _fk_keys="[]"
  _fk_err=$(mktemp) || return 1
  while IFS= read -r _fk_line || [ -n "$_fk_line" ]; do
    [ -n "$_fk_line" ] || continue
    _fk_stdin=$(printf '%s' "$_fk_line" | jq -c '{
      method: .method,
      url: .url,
      headers: (.reqHeaders // []),
      body: (if .reqBodyEncoding == "base64" then (.reqBody // "") else (.reqBody // "" | @base64) end),
      bodyEncoding: "base64"
    }' 2>/dev/null)
    if [ -z "$_fk_stdin" ]; then
      echo "could not parse a recorded request fixture line" >&2
      rm -f "$_fk_err"
      return 1
    fi
    _fk_key1=$(printf '%s' "$_fk_stdin" | sh tests/serialize-request 2>"$_fk_err") || {
      echo "tests/serialize-request failed: $(head -c 500 "$_fk_err")" >&2
      rm -f "$_fk_err"
      return 1
    }
    _fk_key2=$(printf '%s' "$_fk_stdin" | sh tests/serialize-request 2>"$_fk_err") || {
      echo "tests/serialize-request failed: $(head -c 500 "$_fk_err")" >&2
      rm -f "$_fk_err"
      return 1
    }
    if [ "$_fk_key1" != "$_fk_key2" ]; then
      _fk_req=$(printf '%s' "$_fk_line" | jq -r '"\\(.method) \\(.url)"' 2>/dev/null)
      echo "tests/serialize-request produced different output on two runs for $_fk_req; make it deterministic" >&2
      rm -f "$_fk_err"
      return 1
    fi
    _fk_keys=$(printf '%s' "$_fk_keys" | jq -c --arg key "$_fk_key1" '. + [$key]')
  done < "$_fk_file"
  rm -f "$_fk_err"
  if ! printf '%s' "$_fk_keys" | jq -c '{keys: .}' \\
    | env -u LD_PRELOAD -u BUN_OPTIONS -u ${STEP_TEST_FROZEN_TIME_ENV} \\
      curl -fsS -X POST --max-time 10 -H 'content-type: application/json' \\
      -d @- "$_fk_base/fixture-keys?test=${CAPTURE_FIXTURE_SET_NAME}" >/dev/null 2>&1; then
    echo "failed to register fixture keys with the proxy" >&2
    return 1
  fi
  printf '%s\\tfixtureKeys\\t%s\\n' "$SENTINEL" "$(printf '%s' "$_fk_keys" | base64 | tr -d '\\n')"
}

for _vol in $_VOLUMES; do
  _clear_volume "$_vol" || exit 1
done
_seed_volumes "$_SPEC" || exit 1

if [ -f "${STEP_TEST_FROZEN_TIME_FILE}" ]; then
  ${STEP_TEST_FROZEN_TIME_ENV}="$(cat "${STEP_TEST_FROZEN_TIME_FILE}")"
  export ${STEP_TEST_FROZEN_TIME_ENV}
  if [ -f "$FREEZE_SHIM" ]; then
    export LD_PRELOAD="$FREEZE_SHIM"
  fi
else
  unset ${STEP_TEST_FROZEN_TIME_ENV}
  unset LD_PRELOAD
fi

_SERIALIZED_DIR=".step-test-serialized"
if [ -f tests/serialize ]; then
  mkdir -p "$_SERIALIZED_DIR"
  for _vol in $_RO_VOLUMES; do
    if ! _serialize_volume "$_vol" "$_SERIALIZED_DIR/$_vol.pre"; then
      echo "tests/serialize failed for volume $_vol" >&2
      exit 1
    fi
  done
fi

_requests_file="${STEP_TEST_REQUESTS_FILE}"
_has_requests=0
[ -f "$_requests_file" ] && _has_requests=1
_HAS_REQUEST_SERIALIZER=0
[ -f "${STEP_TEST_REQUEST_SERIALIZER_FILE}" ] && _HAS_REQUEST_SERIALIZER=1

if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
  _compute_and_register_fixture_keys "$_requests_file" || exit 1
fi

_use_normalize_helper=0
if [ "$_has_requests" = 1 ] && [ "$_HAS_REQUEST_SERIALIZER" = 1 ]; then
  _use_normalize_helper=1
  _normalize_helper_start
fi
[ "$_has_requests" = 1 ] && _replay_ctl begin "$_SPEC" >/dev/null

_input_file=".step-test-capture-input"
_output_file=".step-test-capture-output"
cat > "$_input_file"
if sh -c "$_STEP_CMD" < "$_input_file" > "$_output_file"; then
  _status=0
else
  _status=$?
fi

_replay_message=""
[ "$_has_requests" = 1 ] && _replay_message=$(_replay_ctl end "$_SPEC")
[ "$_use_normalize_helper" = 1 ] && _normalize_helper_stop
if [ -n "$_replay_message" ]; then
  echo "${STEP_TEST_REQUESTS_DIVERGED_PREFIX}$_replay_message" >&2
  exit 1
fi

printf '%s\\tstatus\\t%s\\n' "$SENTINEL" "$_status"
printf '%s\\tstdout\\t' "$SENTINEL"
_base64 < "$_output_file"
printf '\\n'

if [ "$_status" = 0 ]; then
  # Two back-to-back serializer runs must agree, so a nondeterministic serializer fails the pin here rather than flaking at replay time.
  if [ -f tests/serialize ]; then
    for _vol in $_VOLUMES; do
      _post1="$_SERIALIZED_DIR/$_vol.post1"
      _post2="$_SERIALIZED_DIR/$_vol.post2"
      if ! _serialize_volume "$_vol" "$_post1" || ! _serialize_volume "$_vol" "$_post2"; then
        echo "tests/serialize failed for volume $_vol" >&2
        exit 1
      fi
      if ! cmp -s "$_post1" "$_post2"; then
        echo "serializer for volume $_vol produced different output on two runs; make tests/serialize deterministic" >&2
        exit 1
      fi
      if _is_ro_volume "$_vol" && ! cmp -s "$_SERIALIZED_DIR/$_vol.pre" "$_post1"; then
        echo "step wrote to read-only volume $_vol" >&2
        exit 1
      fi
      printf '%s\\tserialized\\t%s\\t' "$SENTINEL" "$_vol"
      _base64 < "$_post1"
      printf '\\n'
    done
  else
    for _vol in $_RO_VOLUMES; do
      _initial_vol="$(_volume_root "$_SPEC")/initial/$_vol"
      [ -d "$_initial_vol" ] || continue
      _volume_message=""
      _compare_volume_against "$_vol" "$_initial_vol"
      if [ -n "$_volume_message" ]; then
        echo "step wrote to read-only volume $_vol: $_volume_message" >&2
        exit 1
      fi
    done
    for _vol in $_VOLUMES; do
      _is_ro_volume "$_vol" && continue
      _root="$STORAGE_ROOT/$_vol"
      [ -d "$_root" ] || continue
      find "$_root" -type f -not \\( -name '*-wal' -o -name '*-shm' \\) | sort | while IFS= read -r _file; do
        _rel=\${_file#$_root/}
        _dump_out=".step-test-capture-dump"
        if _is_sqlite_file "$_file" && _sqlite_canonical_dump "$_file" "$_dump_out"; then
          printf '%s\\tsqlitedump\\t%s\\t%s\\t' "$SENTINEL" "$_vol" "$_rel"
          _base64 < "$_dump_out"
          rm -f "$_dump_out"
        else
          printf '%s\\tfile\\t%s\\t%s\\t' "$SENTINEL" "$_vol" "$_rel"
          _base64 < "$_file"
        fi
        printf '\\n'
      done
    done
  fi
fi

exit "$_status"
`;

export function assembleStepTestCaptureFiles(args: {
  codeFiles: StepFile[];
  testFiles: StepFile[];
  initialVolumeFiles: StepFile[];
  stepCommand: string;
  volumes: readonly string[];
  roVolumes: readonly string[];
  frozenTime?: string;
  requestFixtures?: string;
}): StepFile[] {
  return [
    ...args.codeFiles,
    ...args.testFiles,
    ...args.initialVolumeFiles,
    { path: STEP_TEST_CAPTURE_RUNNER_FILE, content: STEP_TEST_CAPTURE_SH },
    { path: STEP_COMMAND_FILE, content: args.stepCommand },
    { path: STEP_TEST_VOLUME_LIST_FILE, content: args.volumes.join("\n") },
    { path: STEP_TEST_RO_VOLUME_LIST_FILE, content: args.roVolumes.join("\n") },
    ...(args.frozenTime !== undefined
      ? [{ path: STEP_TEST_FROZEN_TIME_FILE, content: args.frozenTime }]
      : []),
    ...(args.requestFixtures !== undefined
      ? [{ path: STEP_TEST_REQUESTS_FILE, content: args.requestFixtures }]
      : []),
  ];
}

export type StepTestCaptureResult = {
  status: number;
  stdout: string;
  volumeFiles: {
    volume: string;
    path: string;
    content: string;
    encoding: "base64" | "sqlite-dump" | "serialized";
  }[];
  /** Per-fixture serializer match keys, in recorded order, when tests/serialize-request exists. */
  requestFixtureKeys?: string[];
};

export function parseStepTestCapture(output: string): StepTestCaptureResult {
  let status: number | null = null;
  let stdout: string | null = null;
  const volumeFiles: StepTestCaptureResult["volumeFiles"] = [];
  let requestFixtureKeys: string[] | undefined;
  for (const line of output.split("\n")) {
    if (!line.startsWith(`${STEP_TEST_CAPTURE_SENTINEL}\t`)) continue;
    const parts = line.split("\t");
    const kind = parts[1];
    if (kind === "status") {
      status = Number.parseInt(parts[2] ?? "", 10);
    } else if (kind === "stdout") {
      stdout = Buffer.from(parts[2] ?? "", "base64").toString("utf-8");
    } else if (kind === "fixtureKeys") {
      requestFixtureKeys = JSON.parse(
        Buffer.from(parts[2] ?? "", "base64").toString("utf-8")
      );
    } else if (kind === "file") {
      volumeFiles.push({
        volume: parts[2] ?? "",
        path: parts[3] ?? "",
        content: parts[4] ?? "",
        encoding: "base64",
      });
    } else if (kind === "sqlitedump") {
      volumeFiles.push({
        volume: parts[2] ?? "",
        path: parts[3] ?? "",
        content: Buffer.from(parts[4] ?? "", "base64").toString("utf-8"),
        encoding: "sqlite-dump",
      });
    } else if (kind === "serialized") {
      const volume = parts[2] ?? "";
      const bytes = Buffer.from(parts[3] ?? "", "base64");
      if (!bytesAreUtf8Text(bytes)) {
        throw new Error(
          `serializer output for volume ${volume} is not valid UTF-8 text; make tests/serialize print text`
        );
      }
      volumeFiles.push({
        volume,
        path: "",
        content: bytes.toString("utf-8"),
        encoding: "serialized",
      });
    }
  }
  if (status === null || stdout === null) {
    throw new Error(
      "Step test capture output was missing its sentinel payload"
    );
  }
  return {
    status,
    stdout,
    volumeFiles,
    ...(requestFixtureKeys !== undefined ? { requestFixtureKeys } : {}),
  };
}
